import { memo } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { color, EL_COLORS } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { TextIcon } from '@/UI/Icons/TextIcon';
import { ImageIcon } from '@/UI/Icons/ImageIcon';
import { PANEL_HEADER_HEIGHT, PANEL_WIDTH } from '@/components/Recipe/consts/ui';
import { useFlowState, useNodeData } from '@/components/Recipe/FlowContext';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { CanvasIcon } from '@/UI/Icons';
import { imageLayerPropertyLayout, textLayerPropertyLayout } from './layerProperties';
import { LayerPropertiesSection } from './LayerPropertySection';
import { CanvasLayerProperties } from './CanvasLayerProperties';
import type { NodeId } from 'web';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';

interface LayerPropertiesPanelProps {
  nodeId: NodeId;
}

export const LayerPropertyPanel = memo(function LayerPropertyPanel({ nodeId }: LayerPropertiesPanelProps) {
  const { t } = useTranslation();
  const { layers } = useNodeData<CompositorNodeV3>(nodeId).data;
  const layerId = useFlowState((s) => s.compositor[nodeId]?.selectedLayer);
  const layer = layerId !== undefined ? layers[layerId] : undefined;

  return (
    <FlexCol
      className="wea-no-scrollbar"
      data-testid="layer-property-panel"
      sx={{
        background: color.Black92,
        borderLeft: `1px solid ${EL_COLORS.DividerBorder}`,
        height: '100%',
        flexShrink: 0,
        overflowY: 'auto',
        width: PANEL_WIDTH,
      }}
    >
      <FlexCenVer sx={{ px: 2, height: PANEL_HEADER_HEIGHT, gap: 1, flexShrink: 0 }}>
        {layer?.kind.type === 'text' ? (
          <TextIcon style={{ width: 16, height: 16 }} />
        ) : layer?.kind.type === 'image' ? (
          <ImageIcon style={{ width: 16, height: 16 }} />
        ) : (
          <CanvasIcon style={{ width: 16, height: 16 }} />
        )}
        <Typography variant="body-sm-md">
          {layer?.name ?? t(I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.CANVAS_LAYER_NAME)}
        </Typography>
      </FlexCenVer>
      <AppDivider />

      <FlexCol data-testid="layer-property-panel-container" sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
        {layerId === undefined || layer === undefined ? (
          <CanvasLayerProperties nodeId={nodeId} />
        ) : layer?.kind.type === 'text' ? (
          textLayerPropertyLayout.map((section, index) => (
            <LayerPropertiesSection
              key={index}
              index={index}
              section={section}
              nodeId={nodeId}
              layerId={layerId}
              layer={layer}
            />
          ))
        ) : (
          imageLayerPropertyLayout.map((section, index) => (
            <LayerPropertiesSection
              key={index}
              index={index}
              section={section}
              nodeId={nodeId}
              layerId={layerId}
              layer={layer}
            />
          ))
        )}
      </FlexCol>
    </FlexCol>
  );
});
